import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { diffLines } from '../lineDiff';

interface PromptDiffProps {
    before: string;
    after: string;
    beforeLabel?: string;
    afterLabel?: string;
}

interface Row {
    left: { text: string; changed: boolean } | null;
    right: { text: string; changed: boolean } | null;
}

/** Pairs each run of removed lines with the run of added lines that follows. */
function toRows(before: string, after: string): Row[] {
    const rows: Row[] = [];
    let removed: string[] = [];
    let added: string[] = [];
    const flush = () => {
        const count = Math.max(removed.length, added.length);
        for (let i = 0; i < count; i++) {
            rows.push({
                left: i < removed.length ? { text: removed[i], changed: true } : null,
                right: i < added.length ? { text: added[i], changed: true } : null,
            });
        }
        removed = [];
        added = [];
    };
    for (const line of diffLines(before, after)) {
        if (line.type === 'remove') removed.push(line.text);
        else if (line.type === 'add') added.push(line.text);
        else {
            flush();
            rows.push({
                left: { text: line.text, changed: false },
                right: { text: line.text, changed: false },
            });
        }
    }
    flush();
    return rows;
}

const cellSx = (
    cell: Row['left'],
    color: string
): Record<string, unknown> => ({
    px: 1,
    minWidth: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    backgroundColor: !cell
        ? 'action.hover'
        : cell.changed
          ? color
          : undefined,
});

export const PromptDiff: React.FC<PromptDiffProps> = ({
    before,
    after,
    beforeLabel,
    afterLabel,
}) => {
    const rows = useMemo(() => toRows(before, after), [before, after]);
    if (before === after) {
        return (
            <Typography variant="body2" color="text.secondary">
                No differences.
            </Typography>
        );
    }
    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 480,
                overflow: 'auto',
            }}
        >
            {(beforeLabel || afterLabel) && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="caption" sx={{ px: 1 }}>
                        {beforeLabel}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ px: 1, borderLeft: 1, borderColor: 'divider' }}
                    >
                        {afterLabel}
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    fontFamily: 'monospace',
                    fontSize: 13,
                }}
            >
                {rows.map((row, index) => (
                    <React.Fragment key={index}>
                        <Box sx={cellSx(row.left, 'rgba(248, 81, 73, 0.25)')}>
                            {row.left?.text}
                        </Box>
                        <Box
                            sx={{
                                ...cellSx(row.right, 'rgba(46, 160, 67, 0.25)'),
                                borderLeft: 1,
                                borderColor: 'divider',
                            }}
                        >
                            {row.right?.text}
                        </Box>
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};
